// CB:BEGIN homeowner-review-modal
import React, { useState } from 'react';
import { useApi } from '../lib/useApi';

interface ReviewModalProps {
  projectId: string;
  contractorName?: string | null;
  onClose: () => void;
  onSubmitted: () => void;
}

interface ReviewForm {
  // A. Platform
  easeOfUseRating: number | null;
  clarityOfProcessRating: number | null;
  platformCommunicationRating: number | null;
  wouldUseCoreBidsAgain: boolean | null;
  // B. Project
  overallProjectRating: number | null;
  timelineRating: number | null;
  expectationMatchRating: number | null;
  finalOutcomeRating: number | null;
  // C. Contractor
  overallContractorRating: number | null;
  contractorCommunicationRating: number | null;
  professionalismRating: number | null;
  punctualityRating: number | null;
  workmanshipRating: number | null;
  wouldHireContractorAgain: boolean | null;
  wouldRecommendContractor: boolean | null;
  // D. Written
  publicReviewText: string;
  projectHighlightsText: string;
  improvementFeedbackText: string;
  privateFeedbackText: string;
}

const INITIAL: ReviewForm = {
  easeOfUseRating: null, clarityOfProcessRating: null, platformCommunicationRating: null, wouldUseCoreBidsAgain: null,
  overallProjectRating: null, timelineRating: null, expectationMatchRating: null, finalOutcomeRating: null,
  overallContractorRating: null, contractorCommunicationRating: null, professionalismRating: null,
  punctualityRating: null, workmanshipRating: null, wouldHireContractorAgain: null, wouldRecommendContractor: null,
  publicReviewText: '', projectHighlightsText: '', improvementFeedbackText: '', privateFeedbackText: '',
};

// Star rating widget
function StarRating({ value, onChange, label }: { value: number | null; onChange: (v: number) => void; label: string }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const display = hovered ?? value ?? 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <span
            key={n}
            role="button"
            aria-label={`${n} star`}
            style={{
              fontSize: 24,
              cursor: 'pointer',
              color: n <= display ? '#f59e0b' : '#d1d5db',
              transition: 'color 0.1s',
              userSelect: 'none',
            }}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onChange(n)}
          >
            ★
          </span>
        ))}
        {value && <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8, alignSelf: 'center' }}>{value}/5</span>}
      </div>
    </div>
  );
}

// Yes/No toggle
function YesNo({ value, onChange, label }: { value: boolean | null; onChange: (v: boolean) => void; label: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        {[true, false].map(v => (
          <button
            key={String(v)}
            type="button"
            onClick={() => onChange(v)}
            style={{
              padding: '4px 16px',
              borderRadius: 6,
              border: `2px solid ${value === v ? '#3b82f6' : '#d1d5db'}`,
              background: value === v ? '#eff6ff' : 'white',
              color: value === v ? '#1d4ed8' : '#374151',
              fontWeight: value === v ? 600 : 400,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {v ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    </div>
  );
}

const sectionHeader = (title: string) => (
  <h4 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '20px 0 12px', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
    {title}
  </h4>
);

export const ReviewModal: React.FC<ReviewModalProps> = ({ projectId, contractorName, onClose, onSubmitted }) => {
  const { post } = useApi();
  const [form, setForm] = useState<ReviewForm>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const set = (key: keyof ReviewForm, value: unknown) => setForm(f => ({ ...f, [key]: value }));

  // Required: the three "overall" ratings
  const requiredFilled =
    form.overallContractorRating !== null &&
    form.overallProjectRating !== null &&
    form.easeOfUseRating !== null;

  const handleSubmit = async () => {
    if (!requiredFilled) { setError('Please fill in the required ratings (marked with *).'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await post(`/api/projects/${projectId}/review`, form);
      setSuccess(true);
      setTimeout(() => { onSubmitted(); onClose(); }, 1800);
    } catch (e: any) {
      const msg = e?.message || '';
      // Handle 409 — review already exists
      if (msg.includes('already been submitted') || e?.statusCode === 409) {
        setSuccess(true);
        setTimeout(() => { onSubmitted(); onClose(); }, 1500);
        return;
      }
      setError(msg || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Leave a Review</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>
        {contractorName && (
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Project completed by <strong>{contractorName}</strong></p>
        )}
        <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>Fields marked * are required.</p>

        {success && (
          <div style={{ background: '#d1fae5', color: '#065f46', padding: '12px 16px', borderRadius: 8, marginBottom: 12, fontWeight: 600 }}>
            ✓ Thank you! Your review has been submitted.
          </div>
        )}
        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* A. Platform */}
        {sectionHeader('A. CoreBids Platform')}
        <StarRating value={form.easeOfUseRating} onChange={v => set('easeOfUseRating', v)} label="Ease of use *" />
        <StarRating value={form.clarityOfProcessRating} onChange={v => set('clarityOfProcessRating', v)} label="Clarity of process" />
        <StarRating value={form.platformCommunicationRating} onChange={v => set('platformCommunicationRating', v)} label="Platform communication" />
        <YesNo value={form.wouldUseCoreBidsAgain} onChange={v => set('wouldUseCoreBidsAgain', v)} label="Would you use CoreBids again?" />

        {/* B. Project */}
        {sectionHeader('B. Project Overall')}
        <StarRating value={form.overallProjectRating} onChange={v => set('overallProjectRating', v)} label="Overall project rating *" />
        <StarRating value={form.timelineRating} onChange={v => set('timelineRating', v)} label="Timeline / schedule" />
        <StarRating value={form.expectationMatchRating} onChange={v => set('expectationMatchRating', v)} label="Met your expectations" />
        <StarRating value={form.finalOutcomeRating} onChange={v => set('finalOutcomeRating', v)} label="Final outcome / result" />

        {/* C. Contractor */}
        {sectionHeader(`C. Contractor${contractorName ? ` (${contractorName})` : ''}`)}
        <StarRating value={form.overallContractorRating} onChange={v => set('overallContractorRating', v)} label="Overall contractor rating *" />
        <StarRating value={form.contractorCommunicationRating} onChange={v => set('contractorCommunicationRating', v)} label="Communication" />
        <StarRating value={form.professionalismRating} onChange={v => set('professionalismRating', v)} label="Professionalism" />
        <StarRating value={form.punctualityRating} onChange={v => set('punctualityRating', v)} label="Punctuality" />
        <StarRating value={form.workmanshipRating} onChange={v => set('workmanshipRating', v)} label="Workmanship / quality" />
        <YesNo value={form.wouldHireContractorAgain} onChange={v => set('wouldHireContractorAgain', v)} label="Would you hire this contractor again?" />
        <YesNo value={form.wouldRecommendContractor} onChange={v => set('wouldRecommendContractor', v)} label="Would you recommend this contractor?" />

        {/* D. Written */}
        {sectionHeader('D. Written Feedback')}
        {([
          ['publicReviewText', 'Public review (visible to contractor and other homeowners)'],
          ['projectHighlightsText', 'Project highlights — what went well?'],
          ['improvementFeedbackText', 'What could be improved?'],
          ['privateFeedbackText', 'Private feedback (CoreBids admin only — not shown publicly)'],
        ] as [keyof ReviewForm, string][]).map(([key, label]) => (
          <div key={key} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 4 }}>{label}</label>
            <textarea
              rows={3}
              value={form[key] as string}
              onChange={e => set(key, e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Optional..."
            />
          </div>
        ))}

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={handleSubmit}
            disabled={submitting || !requiredFilled || success}
          >
            {submitting ? 'Submitting…' : 'Submit Review'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
// CB:END homeowner-review-modal
